import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase-config';

const News = () => {
  const [news, setNews] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, featured: 0 });

  useEffect(() => {
    const loadNews = async () => {
      try {
        const q = query(collection(db, 'news'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const newsData = [];
        snapshot.forEach(doc => newsData.push({ id: doc.id, ...doc.data() }));
        setNews(newsData);

        const featured = newsData.filter(n => n.featured);
        setStats({ total: newsData.length, featured: featured.length });
      } catch (error) {
        console.error('Error loading news:', error);
      }
    };
    loadNews();
  }, []);

  // Helper function to get image URL from news item
  const getImageUrl = (item) => {
    if (!item) return null;
    // Try different field names that might contain the image
    return item.imageUrl || item.image || item.featuredImage || item.banner || null;
  };

  // Get fallback image
  const getFallbackImage = () => {
    return 'https://images.unsplash.com/photo-1562774053-701939374585?w=400';
  };

  const filteredNews = news.filter(item => {
    const matchesFilter = filter === 'all' || item.category === filter;
    const matchesSearch = item.title?.toLowerCase().includes(search.toLowerCase()) ||
                         item.description?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const categories = ['all', ...new Set(news.map(item => item.category).filter(Boolean))];

  return (
    <div className="container news-page">
      <motion.h1
        className="news-page-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <i className="fas fa-newspaper" style={{ color: 'var(--clr-blue)' }}></i> News & Updates
      </motion.h1>
      <p className="news-page-subtitle">Stay informed about the latest happenings at Dolphin Institute</p>

      {/* Stats */}
      <div className="news-stats">
        <div className="news-stat-card stat-blue">
          <div className="news-stat-value">{stats.total}+</div>
          <div className="news-stat-label">Total Articles</div>
        </div>
        <div className="news-stat-card stat-emerald">
          <div className="news-stat-value">{stats.featured}+</div>
          <div className="news-stat-label">Featured Stories</div>
        </div>
        <div className="news-stat-card stat-amber">
          <div className="news-stat-value">{new Date().getFullYear()}</div>
          <div className="news-stat-label">Current Year</div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="news-search-section">
        <div className="news-search-box">
          <div className="news-search-wrapper">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search news..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="news-search-input"
            />
          </div>
        </div>
        <div className="news-filter-buttons">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`news-filter-btn ${filter === cat ? 'active' : ''}`}
            >
              {cat === 'all' ? 'All News' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* News List */}
      <div className="news-list">
        {filteredNews.length === 0 ? (
          <div className="news-empty">
            <i className="fas fa-search"></i>
            <p>No news found matching your criteria</p>
          </div>
        ) : (
          filteredNews.map((item, index) => {
            const imageUrl = getImageUrl(item) || getFallbackImage();
            return (
              <motion.div
                key={item.id}
                className="news-item-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ x: 8, boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)' }}
              >
                <Link to={`/news/${item.id}`} className="news-item-link">
                  <div className="news-item-image">
                    <img
                      src={imageUrl}
                      alt={item.title || 'News image'}
                      onError={(e) => {
                        e.target.src = getFallbackImage();
                      }}
                    />
                  </div>
                  <div className="news-item-content">
                    <div className="news-item-tags">
                      {item.category && (
                        <span className="news-tag category-tag">{item.category}</span>
                      )}
                      {item.featured && (
                        <span className="news-tag featured-tag"><i className="fas fa-star"></i> Featured</span>
                      )}
                    </div>
                    <h3 className="news-item-title">{item.title || 'News Update'}</h3>
                    <p className="news-item-description">
                      {(item.description || '').substring(0, 150)}...
                    </p>
                    <div className="news-item-meta">
                      <span><i className="far fa-calendar-alt"></i> {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'Recent'}</span>
                      <span><i className="far fa-clock"></i> {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      <span className="news-read-more">Read full story →</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Add CSS for responsiveness */}
      <style>{`
        /* News Page Styles */
        .news-page {
          padding-top: 40px;
        }

        .news-page-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          margin-bottom: 10px;
        }

        .news-page-subtitle {
          color: var(--text-secondary);
          margin-bottom: 30px;
        }

        /* Stats */
        .news-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 40px;
        }

        .news-stat-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 16px;
          padding: 24px;
          text-align: center;
          border-bottom: 3px solid var(--clr-blue);
        }

        .stat-blue {
          border-bottom-color: var(--clr-blue);
        }
        .stat-emerald {
          border-bottom-color: var(--clr-emerald);
        }
        .stat-amber {
          border-bottom-color: var(--clr-amber);
        }

        .news-stat-value {
          font-family: 'Playfair Display', serif;
          font-size: 2rem;
          font-weight: 800;
          color: var(--clr-blue);
        }

        .stat-blue .news-stat-value {
          color: var(--clr-blue);
        }
        .stat-emerald .news-stat-value {
          color: var(--clr-emerald);
        }
        .stat-amber .news-stat-value {
          color: var(--clr-amber);
        }

        .news-stat-label {
          font-size: 0.85rem;
          font-weight: 700;
          margin-top: 5px;
        }

        /* Search & Filter */
        .news-search-section {
          margin-bottom: 30px;
        }

        .news-search-box {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }

        .news-search-wrapper {
          flex: 1;
          position: relative;
        }

        .news-search-wrapper i {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
        }

        .news-search-input {
          width: 100%;
          padding: 14px 16px 14px 45px;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border: 1px solid var(--clr-blue-border);
          border-radius: 50px;
          font-size: 0.9rem;
          outline: none;
          transition: all 0.3s ease;
        }

        .news-search-input:focus {
          border-color: var(--clr-blue);
          background: rgba(255, 255, 255, 0.8);
        }

        .news-filter-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .news-filter-btn {
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(20px) saturate(140%);
          border: 1px solid var(--clr-blue-border);
          border-radius: 40px;
          color: var(--text-secondary);
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .news-filter-btn.active {
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          color: white;
          border: none;
        }

        .news-filter-btn:hover:not(.active) {
          background: rgba(255, 255, 255, 0.7);
        }

        /* News List */
        .news-list {
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin-bottom: 60px;
        }

        .news-item-card {
          display: flex;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid var(--clr-blue-border);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .news-item-link {
          display: flex;
          width: 100%;
          text-decoration: none;
          color: inherit;
        }

        .news-item-image {
          flex: 0 0 200px;
          height: 200px;
          overflow: hidden;
        }

        .news-item-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s;
        }

        .news-item-card:hover .news-item-image img {
          transform: scale(1.05);
        }

        .news-item-content {
          flex: 1;
          padding: 20px 20px 20px 0;
          display: flex;
          flex-direction: column;
        }

        .news-item-tags {
          margin-bottom: 12px;
        }

        .news-tag {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 700;
          margin-right: 8px;
        }

        .category-tag {
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          color: white;
        }

        .featured-tag {
          background: var(--clr-amber);
          color: white;
        }

        .news-item-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.2rem;
          margin-bottom: 10px;
        }

        .news-item-description {
          color: var(--text-secondary);
          font-size: 0.85rem;
          line-height: 1.6;
          margin-bottom: 15px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .news-item-meta {
          margin-top: auto;
          padding-top: 12px;
          font-size: 0.75rem;
          color: var(--text-muted);
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .news-read-more {
          color: var(--clr-blue);
        }

        .news-empty {
          text-align: center;
          padding: 60px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 24px;
        }

        .news-empty i {
          font-size: 2rem;
          display: block;
          margin-bottom: 10px;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .news-page {
            padding-top: 20px;
          }

          .news-page-title {
            font-size: 1.8rem;
          }

          .news-stats {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
            margin-bottom: 25px;
          }

          .news-stat-card {
            padding: 16px 12px;
          }

          .news-stat-value {
            font-size: 1.5rem;
          }

          .news-stat-label {
            font-size: 0.7rem;
          }

          .news-search-box {
            flex-direction: column;
            gap: 10px;
          }

          .news-filter-buttons {
            gap: 8px;
          }

          .news-filter-btn {
            padding: 8px 16px;
            font-size: 0.8rem;
          }

          .news-item-card {
            flex-direction: column;
          }

          .news-item-link {
            flex-direction: column;
          }

          .news-item-image {
            flex: 0 0 200px;
            width: 100%;
            height: 200px;
          }

          .news-item-content {
            padding: 16px;
          }

          .news-item-title {
            font-size: 1.1rem;
          }

          .news-item-description {
            font-size: 0.8rem;
            -webkit-line-clamp: 3;
          }

          .news-item-meta {
            font-size: 0.7rem;
            gap: 12px;
          }

          .news-empty {
            padding: 40px 20px;
          }
        }

        @media (max-width: 480px) {
          .news-page-title {
            font-size: 1.5rem;
          }

          .news-stats {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .news-stat-card {
            padding: 14px 10px;
          }

          .news-stat-value {
            font-size: 1.3rem;
          }

          .news-stat-label {
            font-size: 0.65rem;
          }

          .news-filter-btn {
            padding: 6px 14px;
            font-size: 0.75rem;
          }

          .news-item-image {
            flex: 0 0 160px;
            height: 160px;
          }

          .news-item-content {
            padding: 14px;
          }

          .news-item-title {
            font-size: 1rem;
          }

          .news-item-description {
            font-size: 0.75rem;
            -webkit-line-clamp: 2;
          }

          .news-item-meta {
            font-size: 0.65rem;
            gap: 10px;
            flex-wrap: wrap;
          }

          .news-tag {
            font-size: 0.6rem;
            padding: 3px 10px;
          }
        }

        @media (max-width: 360px) {
          .news-stats {
            grid-template-columns: 1fr;
          }

          .news-stat-card {
            padding: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default News;